/**
 * Aero Sites, single-version page.
 *
 * Routed at /sites/:siteId/versions/:versionId as a FLAT SIBLING of the list
 * and detail routes under AeroLayout, so it receives { user } from the outlet
 * context directly, exactly like AeroSites and AeroSiteDetail.
 *
 * Shows one immutable version: an overview card (created, files, size,
 * change), a live preview iframe ONLY when this version is the site's
 * activeVersion, a replace-a-file dropzone, and the full file list from the
 * version's manifest. Dropping a single file (on a specific row, or on the
 * dropzone where it is matched by filename) calls deployForkedVersion, which
 * writes a NEW draft version; this page never edits the version it shows.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import GlassButton from '../../components/Aero/GlassButton';
import AeroStatePanel, { stateFromError } from '../../components/Aero/AeroStatePanel';
import AeroSitePreview from '../../components/Aero/AeroSitePreview';
import { AeroStatus } from '../../components/Aero/AeroHomeParts';
import firebase from '../../firebase';
import { deployForkedVersion } from '../../utils/htmlDeploy/deploySite';
import { describeChange, formatVersionDate, formatBytes } from './sitesUtils';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';
import './AeroSites.css';

function AeroSiteVersion() {
  const { user } = useOutletContext();
  const { siteId, versionId } = useParams();
  const navigate = useNavigate();
  const db = firebase.firestore();

  const [site, setSite] = useState(null);
  // Real owner uid so a version fork on a SHARED site writes to the owner's
  // path, not the logged-in admin's.
  const [ownerUid, setOwnerUid] = useState(null);
  const [version, setVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forking, setForking] = useState(false);
  const [dropError, setDropError] = useState(null);
  const [dragPath, setDragPath] = useState(null);
  const [dragZone, setDragZone] = useState(false);
  const [copiedPath, setCopiedPath] = useState(null);

  // Load the site doc, this version doc, and the ordered versions list (the
  // list is only needed to decide the Draft badge, same rule as the History
  // card on the detail page).
  const fetchAll = useCallback(async () => {
    if (!user?.uid || !siteId || !versionId) {
      setLoading(false);
      return;
    }
    try {
      // Resolve the owner: try the caller's own path, else find the site among
      // those shared with this admin (collection-group by sharedWith).
      let owner = user.uid;
      let siteData = null;
      const ownedSnap = await db
        .collection('user').doc(user.uid)
        .collection('url').doc(siteId).get();
      if (ownedSnap.exists) {
        siteData = ownedSnap.data();
      } else {
        const sharedSnap = await db
          .collectionGroup('url')
          .where('sharedWith', 'array-contains', user.uid)
          .get()
          .catch(() => ({ docs: [] }));
        const match = sharedSnap.docs.find((d) => d.id === siteId);
        if (match) {
          siteData = match.data();
          owner = match.ref.parent.parent.id;
        }
      }
      if (!siteData || siteData.type !== 'html') {
        setError('site-notfound');
        return;
      }
      const urlRef = db
        .collection('user').doc(owner)
        .collection('url').doc(siteId);
      const [versionSnap, versionsSnap] = await Promise.all([
        urlRef.collection('versions').doc(versionId).get(),
        urlRef.collection('versions').orderBy('createdAt', 'desc').get(),
      ]);
      if (!versionSnap.exists) {
        setError('notfound');
        return;
      }
      setOwnerUid(owner);
      setSite({ id: siteId, ownerUid: owner, ...siteData });
      setVersion({ id: versionSnap.id, ...versionSnap.data() });
      setVersions(versionsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading version:', err);
      setError(stateFromError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, siteId, versionId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    document.title = site ? `${site.title || site.generatedUrl} | Aero Sites` : 'Sites | Aero';
  }, [site]);

  // Replace one file in this version by forking it into a new draft. Called
  // by both the file rows (replacePath given) and the dropzone (replacePath
  // null: match the dropped file by its filename against the manifest).
  const handleReplaceDrop = async (replacePath, fileList) => {
    if (forking || !site || !version) return;
    setDropError(null);
    const files = Array.from(fileList || []).filter((f) => f && f.name);
    if (files.length === 0) return;
    if (files.length > 1) {
      setDropError('Drop a single file at a time. Multi-file drops are ignored here.');
      return;
    }
    const file = files[0];

    let targetPath = replacePath;
    if (!targetPath) {
      // Dropzone drop: match by exact filename (the path's last segment).
      const matches = Object.keys(version.files || {}).filter(
        (p) => p.split('/').pop() === file.name
      );
      if (matches.length === 1) {
        targetPath = matches[0];
      } else if (matches.length === 0) {
        setDropError(
          `No file in this version is named "${file.name}". Drop it on the specific file row instead.`
        );
        return;
      } else {
        setDropError(
          `More than one file is named "${file.name}". Drop it on the specific file row instead.`
        );
        return;
      }
    }

    setForking(true);
    try {
      await deployForkedVersion({
        uid: ownerUid || user.uid,
        urlId: site.id,
        baseVersionId: version.id,
        replacePath: targetPath,
        file,
      });
      toast.success('Draft created. Publish it from Versions.', { autoClose: 8000 });
      navigate(`/sites/${site.id}`);
    } catch (err) {
      console.error('Replace failed:', err);
      setDropError('Could not create the draft: ' + err.message);
    } finally {
      setForking(false);
    }
  };

  const zoneDragProps = {
    onDragOver: (e) => {
      e.preventDefault();
      setDragZone(true);
    },
    onDragLeave: () => setDragZone(false),
    onDrop: (e) => {
      e.preventDefault();
      setDragZone(false);
      handleReplaceDrop(null, e.dataTransfer.files);
    },
  };

  const rowDragProps = (path) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragPath(path);
    },
    onDragLeave: () => setDragPath(null),
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragPath(null);
      handleReplaceDrop(path, e.dataTransfer.files);
    },
  });

  const handleCopyUrl = async (path) => {
    try {
      await navigator.clipboard.writeText(`https://${site.generatedUrl}.aeroapp.ai/${path}`);
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Mirror AeroSiteDetail's cold-load handling.
  if (loading) {
    return null;
  }

  if (error || !site || !version) {
    const siteMissing = error === 'site-notfound';
    const kind = siteMissing ? 'notfound' : error || 'notfound';
    const retry = () => { setError(null); setLoading(true); fetchAll(); };
    const back = siteMissing
      ? <GlassButton fill={kind === 'notfound' ? 'ink' : 'glass'} size="sm" onClick={() => navigate('/sites')}>All sites</GlassButton>
      : <GlassButton fill={kind === 'notfound' || kind === 'noaccess' ? 'ink' : 'glass'} size="sm" onClick={() => navigate(`/sites/${siteId}`)}>Back to the site</GlassButton>;
    return (
      <div className="aero-main aero-sites-page">
        <AeroPageHeader crumbs={[{ label: 'Sites', onClick: () => navigate('/sites') }, { label: 'Version' }]} />
        <AeroStatePanel
          kind={kind}
          title={kind === 'notfound' ? (siteMissing ? 'Site not found' : 'Version not found') : kind === 'noaccess' ? "You don't have access to this site" : undefined}
          actions={(kind === 'offline' || kind === 'error') ? (
            <>
              <GlassButton fill="ink" size="sm" onClick={retry}>Try again</GlassButton>
              {back}
            </>
          ) : back}
        >
          {kind === 'notfound' ? (siteMissing ? 'It may have been deleted, or the link may be wrong.' : 'It may have been discarded, or the link may be wrong.') : undefined}
        </AeroStatePanel>
      </div>
    );
  }

  const isActive = version.id === site.activeVersion;
  const activeIndex = versions.findIndex((v) => v.id === site.activeVersion);
  const thisIndex = versions.findIndex((v) => v.id === version.id);
  const isDraft =
    !isActive && (activeIndex === -1 || (thisIndex !== -1 && thisIndex < activeIndex));

  const liveUrl = `https://${site.generatedUrl}.aeroapp.ai`;
  const dateLabel = formatVersionDate(version.createdAt);
  const sizeLabel = formatBytes(version.totalBytes);
  const sortedPaths = Object.keys(version.files || {}).sort();
  const sizes = version.sizes || {};

  return (
    <div className="aero-main aero-sites-page">
      <Helmet>
        <title>{site.title || site.generatedUrl} | Aero Sites</title>
      </Helmet>
      {/* Inside .aero-main so it's the single flex child of .ut-main. */}
      <ToastContainer position="bottom-right" className="aero-toast-container" />

      <AeroPageHeader
        crumbs={[
          { label: 'Sites', onClick: () => navigate('/sites') },
          { label: site.title || site.generatedUrl, onClick: () => navigate(`/sites/${site.id}`) },
          { label: dateLabel },
        ]}
      />

      <div className="aero-sd-body">
      <div className="aero-sd-top aero-sv-top">
        <h2 className="aero-sd-title">{dateLabel}</h2>
        <div className="aero-sd-addr">
          {isActive && <AeroStatus kind="live">Live</AeroStatus>}
          {isDraft && <AeroStatus kind="draft" />}
        </div>
      </div>

      {/* Overview card */}
      <div className="aero-card aero-card-pad aero-sv-overview-card">
        <div className="aero-sv-overview">
          <div>
            <div className="aero-sv-stat-label">Created</div>
            <div className="aero-sv-stat-value">{dateLabel}</div>
          </div>
          <div>
            <div className="aero-sv-stat-label">Files</div>
            <div className="aero-sv-stat-value">{version.fileCount || sortedPaths.length}</div>
          </div>
          <div>
            <div className="aero-sv-stat-label">Size</div>
            <div className="aero-sv-stat-value">{sizeLabel}</div>
          </div>
          <div>
            <div className="aero-sv-stat-label">Change</div>
            <div className="aero-sv-stat-value">{describeChange(version)}</div>
          </div>
        </div>
      </div>

      {/* Two-column row: preview (left) + replace-a-file dropzone (right) */}
      <div className="aero-sites-detail-cols">
        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Preview</div>
          {isActive ? (
            <AeroSitePreview host={`${site.generatedUrl}.aeroapp.ai`} size="sm">
              <iframe
                key={version.id}
                src={liveUrl}
                title={`${site.generatedUrl} live preview`}
                loading="lazy"
              />
            </AeroSitePreview>
          ) : (
            <div className="aero-sv-preview-placeholder">
              <p>Preview is available for the live version.</p>
            </div>
          )}
        </div>

        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Replace a file</div>
          <div
            className={`aero-sites-dropzone aero-sv-dropzone${dragZone ? ' active' : ''}${
              forking ? ' disabled' : ''
            }`}
            {...zoneDragProps}
          >
            {forking ? (
              <p>Creating draft…</p>
            ) : dragZone ? (
              <p>Drop the file to replace it</p>
            ) : (
              <>
                <p>Drop a file here to replace it in this version</p>
                <p className="aero-sites-dropzone-hint">
                  Replacing never edits this version. It creates a new draft you
                  can publish from Versions.
                </p>
              </>
            )}
          </div>
          {dropError && <p className="aero-sv-drop-error">{dropError}</p>}
        </div>
      </div>

      {/* Files card: every path in this version's manifest */}
      <div className="aero-card aero-sv-files">
        <div className="aero-card-head">
          <h3>Files</h3>
        </div>
        <div className="aero-sv-file-list">
          {sortedPaths.map((path) => {
            const fileSize = formatBytes(sizes[path]);
            return (
              <div
                key={path}
                className={`aero-sv-file-row${dragPath === path ? ' drag' : ''}`}
                {...rowDragProps(path)}
              >
                <span className="aero-sv-file-path">{path}</span>
                <span className="aero-sv-file-meta">
                  {fileSize && <span className="aero-sv-file-size">{fileSize}</span>}
                  {isActive && (
                    <button
                      type="button"
                      className="aero-btn aero-btn-secondary aero-btn-sm"
                      onClick={() => handleCopyUrl(path)}
                    >
                      {copiedPath === path ? 'Copied!' : 'Copy URL'}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
}

export default AeroSiteVersion;
